import { CONCEPT_RECENT_CACHE } from "./Config";
import type { ConceptBrief } from "./ConceptType";

interface RecentConceptEntry {
  code: string;
  name: string;
  pinYin?: string;
  pinYinInitial?: string;
}

function storageKey(spaceProductId: string): string {
  return `${CONCEPT_RECENT_CACHE.keyPrefix}.v${CONCEPT_RECENT_CACHE.version}:${spaceProductId}`;
}

function parseEntries(raw: string | null): RecentConceptEntry[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    const out: RecentConceptEntry[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== "object") {
        continue;
      }
      const code = String((item as RecentConceptEntry).code ?? "").trim();
      const name = String((item as RecentConceptEntry).name ?? "").trim();
      if (!code || !name) {
        continue;
      }
      out.push({
        code,
        name,
        pinYin: (item as RecentConceptEntry).pinYin,
        pinYinInitial: (item as RecentConceptEntry).pinYinInitial,
      });
    }
    return out;
  } catch {
    return [];
  }
}

function toBrief(entry: RecentConceptEntry): ConceptBrief {
  return {
    code: entry.code,
    name: entry.name,
    pinYin: entry.pinYin,
    pinYinInitial: entry.pinYinInitial,
  };
}

export function readRecentConcepts(spaceProductId: string): ConceptBrief[] {
  if (typeof localStorage === "undefined" || !spaceProductId.trim()) {
    return [];
  }
  const entries = parseEntries(localStorage.getItem(storageKey(spaceProductId)));
  return entries.map(toBrief);
}

export function recordRecentConcept(spaceProductId: string, concept: ConceptBrief): void {
  if (typeof localStorage === "undefined" || !spaceProductId.trim()) {
    return;
  }
  const code = concept.code.trim();
  const name = concept.name.trim();
  if (!code || !name) {
    return;
  }
  const key = storageKey(spaceProductId);
  const prev = parseEntries(localStorage.getItem(key)).filter((item) => item.code !== code);
  const next: RecentConceptEntry[] = [
    {
      code,
      name,
      pinYin: concept.pinYin,
      pinYinInitial: concept.pinYinInitial,
    },
    ...prev,
  ].slice(0, CONCEPT_RECENT_CACHE.maxItems);
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // ignore quota errors
  }
}
